import { useEffect, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { BthLogo } from '../brand/BthLogo';
import { CloudTalkLiveToastHost } from '../dem/CloudTalkLiveToastHost';
import { DemNotificationsSlot } from '../dem/DemNotificationsSlot';
import { PortalWebPushBanner } from '../notifications/PortalWebPushBanner';
import { GlobalSearch } from '../search/GlobalSearch';
import { MobileBottomNav } from './MobileBottomNav';
import { DemFloatingShell } from './DemFloatingShell';
import { useAuth } from '../../contexts/AuthContext';
import { usePortal } from '../../contexts/PortalContext';
import { PortalSwitcher } from './PortalSwitcher';
import { PortalPresenceReporter } from './PortalPresenceReporter';
import { DEM_ADMIN_MODULES, DEM_DEFAULT_SLUG, DEM_MAIN_MODULES } from '../../lib/demModules';
import { formatDisplayPersonName } from '../../lib/formatPersonName';

/** Floating left-rail theme: Dashboard → CloudTalk (+ nested), theme preview. */
function useDemFloatingChrome(pathname: string): boolean {
  return (
    /\/(analytics|theme-preview)\/?$/.test(pathname) ||
    /\/deliveries(\/|$)/.test(pathname) ||
    /\/orders(\/|$)/.test(pathname) ||
    /\/blanket-orders(\/|$)/.test(pathname) ||
    /\/customer-feedback(\/|$)/.test(pathname) ||
    /\/pricing-update(\/|$)/.test(pathname) ||
    /\/sku-map(\/|$)/.test(pathname) ||
    /\/items(\/|$)/.test(pathname) ||
    /\/customers(\/|$)/.test(pathname) ||
    /\/standard-sales-lines(\/|$)/.test(pathname) ||
    /\/cloudtalk(\/|$)/.test(pathname)
  );
}

export function DemAppShell() {
  const { profile, signOut, role: userRole } = useAuth();
  const { basePath } = usePortal();
  const location = useLocation();
  const floatingChrome = useDemFloatingChrome(location.pathname);
  const welcomeName =
    formatDisplayPersonName(profile?.display_name?.trim() || profile?.username) || 'there';
  const roleLabel = (profile?.role ?? userRole ?? 'user').toUpperCase();
  const home = basePath ? `${basePath}/${DEM_DEFAULT_SLUG}` : `/${DEM_DEFAULT_SLUG}`;

  /* Floating chrome owns nav (menu drawer) — strip dock layout leftovers that
   * reserve ~6.75rem top padding and keep a portaled bottom dock visible. */
  useEffect(() => {
    if (!floatingChrome) return;
    const root = document.documentElement;
    root.classList.remove('has-dock');
    root.style.removeProperty('--dock-clearance');
    root.style.removeProperty('--mobile-header-clearance');
    root.style.setProperty('--dock-clearance', '0px');
    root.style.setProperty('--mobile-header-clearance', '0px');
    return () => {
      root.style.removeProperty('--dock-clearance');
      root.style.removeProperty('--mobile-header-clearance');
    };
  }, [floatingChrome]);

  const mainNav = useMemo(
    () =>
      DEM_MAIN_MODULES.map((mod) => ({
        to: basePath ? `${basePath}/${mod.slug}` : `/${mod.slug}`,
        label: mod.label,
        icon: mod.icon,
      })),
    [basePath],
  );

  const adminNav = useMemo(
    () =>
      DEM_ADMIN_MODULES.map((mod) => ({
        to: basePath ? `${basePath}/${mod.slug}` : `/${mod.slug}`,
        label: mod.label,
        icon: mod.icon,
      })),
    [basePath],
  );

  return (
    <div
      className={`app-shell app-shell--dock app-shell--dem${
        floatingChrome ? ' app-shell--dem-theme-lab' : ''
      }`}
    >
      <PortalPresenceReporter portal="dem" />
      <CloudTalkLiveToastHost />
      <div className="main">
        {!floatingChrome ? (
          <header className="topbar">
            <div className="topbar__brand">
              <BthLogo className="topbar__logo" />
            </div>

            <p className="topbar__title">
              Welcome back, <span className="topbar__name">{welcomeName}</span>!
            </p>

            <div className="topbar__controls">
              <GlobalSearch />
              <div className="topbar__meta" title={`${profile?.display_name ?? 'User'} · ${roleLabel}`}>
                <span className="topbar__role">{roleLabel}</span>
              </div>

              <div className="topbar__actions">
                <DemNotificationsSlot />
                <PortalSwitcher current="dem" />
                <button
                  type="button"
                  className="btn-secondary btn-sm topbar__signout"
                  aria-label="Sign out"
                  onClick={() => void signOut()}
                >
                  <span className="topbar__signout-icon" aria-hidden>
                    ⎋
                  </span>
                  <span className="topbar__signout-label">Sign out</span>
                </button>
              </div>
            </div>
          </header>
        ) : null}

        <PortalWebPushBanner variant={floatingChrome ? 'toast' : 'banner'} />

        <div className="page">
          {floatingChrome ? (
            <DemFloatingShell>
              <Outlet />
            </DemFloatingShell>
          ) : (
            <Outlet />
          )}
        </div>
      </div>

      {/* Floating chrome: menu button opens left panel — no bottom dock. */}
      {!floatingChrome ? (
        <MobileBottomNav
          items={mainNav.map((item) => ({ ...item, end: item.to === home }))}
          extraItems={adminNav}
          showDivider={adminNav.length > 0}
        />
      ) : null}
    </div>
  );
}
